/**
 * Trailing-edge coalescer.
 *
 * The client half of the cursor throttle. The server enforces its own 20 Hz cap
 * per socket (see `handleCursor` in `apps/api/src/durable/board-room.ts`) because
 * a client must not be able to choose its own rate, but relying on the server to
 * do it would mean shipping every one of those frames over the network first -
 * which is the cost that actually matters on a metered connection.
 *
 * ## Why a trailing flush and not a drop
 *
 * A pointer that crosses the board produces a frame every 8-16 ms. A pure drop
 * keeps the first frame of each 50 ms window and discards the rest, which means
 * the position a peer sees is always the *oldest* one in the window - a cursor
 * that lags by up to 50 ms and never catches up while the pointer is moving.
 * Keeping the latest value and flushing it at the end of the window means the
 * cursor is at most one frame behind, which is the property that makes a remote
 * cursor look like it is attached to the same board.
 *
 * ## Why the flush deadline is not pushed out on every push
 *
 * `push` records the deadline on the *first* value of a window and leaves it
 * alone afterwards. A pointer moving continuously would otherwise keep deferring
 * the flush forever and the coalescer would degenerate into a drop - the exact
 * failure it exists to avoid.
 *
 * The timers are injectable so the behaviour is testable without waiting. The
 * test file drives a fake clock; production passes nothing.
 */

export interface Scheduler {
  setTimer(fn: () => void, ms: number): number;
  clearTimer(handle: number): void;
  now(): number;
}

const defaultScheduler: Scheduler = {
  setTimer: (fn, ms) => setTimeout(fn, ms) as unknown as number,
  clearTimer: (handle) => clearTimeout(handle),
  now: () => Date.now(),
};

export class Coalescer<T> {
  private pending: { value: T } | null = null;
  private deadline: number | null = null;
  private timer: number | null = null;

  constructor(
    private readonly intervalMs: number,
    private readonly flush: (value: T) => void,
    private readonly scheduler: Scheduler = defaultScheduler,
  ) {}

  /**
   * Offer a value. Either it goes out immediately - the window is open - or it
   * replaces whatever is buffered and a flush is scheduled for the end of the
   * window.
   */
  push(value: T): void {
    if (this.timer === null) {
      // Window is open. Send now and start a new window.
      this.deadline = this.scheduler.now() + this.intervalMs;
      this.flush(value);
      this.timer = this.scheduler.setTimer(() => this.onWindowEnd(), this.intervalMs);
      return;
    }

    // Window is closed. Buffer the latest value; the existing timer is left
    // alone so the deadline does not move.
    this.pending = { value };
  }

  /**
   * Flush any buffered value now and close the window.
   *
   * Called when a gesture ends - a pointer leaving the board, a tab being hidden
   * - so the final position is delivered without waiting out the window. A tab
   * that is hidden mid-gesture otherwise leaves a peer's cursor stranded a few
   * pixels short, permanently, because no further frame is coming.
   */
  flushNow(): void {
    this.clearTimer();
    const buffered = this.pending;
    this.pending = null;
    this.deadline = null;
    if (buffered !== null) this.flush(buffered.value);
  }

  /** Drop any buffered value without sending it. Used on disconnect, where the
   * position is meaningless to a server that no longer has the socket. */
  cancel(): void {
    this.clearTimer();
    this.pending = null;
    this.deadline = null;
  }

  /** Diagnostics only. Exposed so the connection can report whether a flush is
   * outstanding, which is the kind of thing that is otherwise invisible when a
   * cursor bug is reported. */
  get isWindowOpen(): boolean {
    return this.timer !== null;
  }

  /** The scheduled flush time, or `null`. Diagnostics only. */
  get scheduledFor(): number | null {
    return this.deadline;
  }

  private onWindowEnd(): void {
    this.timer = null;
    this.deadline = null;
    const buffered = this.pending;
    this.pending = null;
    if (buffered !== null) {
      // Re-open the window for the flushed value rather than sending it bare.
      // Sending it bare would leave `timer === null` and the *next* push would
      // go out immediately, letting a client that moves in bursts exceed the
      // intended rate.
      this.push(buffered.value);
    }
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      this.scheduler.clearTimer(this.timer);
      this.timer = null;
    }
  }
}
